import AddIcon from "@mui/icons-material/Add";
import { Button, Typography } from "@mui/material";
import { cloneDeep, concat, get, set } from "lodash";
import React, { FC } from "react";
import CreateForm from "src/components/ui-component/form-component/create-form/CreateForm";
import MUIAccordionComponent from "src/components/ui-component/mui-accordion/MUIAccordionComponent";
import { useApplication } from "src/context/ApplicationContext";
import DeleteRecord from "src/lib/api/DeleteRecord";
import { APIS } from "src/lib/constants/apis";
import {
  KOSTEN_ARTEN,
  KOSTEN_POSITION,
  NACHWEIS_POSITION_STATUS,
} from "src/lib/constants/constant";
import { FormStrucItem } from "src/lib/types/types";
import {
  InhaltTrenner,
  InhaltWrapper,
} from "src/pages/private/online-antrag/antrag-components/Wrapper";
import { addIndexesToPath } from "src/utils/helper";

// import schemas
import MittelabrufSchema from "src/lib/paths/mittelabruf_FULL_Paths.json";
import NachweisPositionSchema from "src/lib/paths/nachweisposition_FULL_Paths.json";
import { belongsToMittelabrufOrganization } from "src/pages/private/online-antrag/structure/mitelabruf/loewe/content/LoeweMittelabrufOrganizations";
const P = MittelabrufSchema.attributes;
const NP_P = NachweisPositionSchema.attributes;

const PersonalausgabenLoeweInputMA: FC<{
  nachweisPositionStatus: string;
  organizationId: number | string;
  kostentraeger: string;
}> = ({ nachweisPositionStatus, organizationId, kostentraeger }) => {
  const { application, setApplicationProperty, saveApplication, readOnly } =
    useApplication();
  const isGeplanteAusgaben =
    nachweisPositionStatus === NACHWEIS_POSITION_STATUS.geplant;
  const sectionTitle = isGeplanteAusgaben
    ? "Geplante Personalausgaben"
    : "Angefallene Personalausgaben";
  const addButtonLabel = isGeplanteAusgaben
    ? "Geplante Personalausgaben hinzufügen"
    : "Angefallene Personalausgaben hinzufügen";
  const statusFilter = isGeplanteAusgaben
    ? NACHWEIS_POSITION_STATUS.geplant
    : NACHWEIS_POSITION_STATUS.bereitsAngefalleneAusgaben;

  const handleAdd = (nachweisPositionStatus: string) => {
    let appCopy = cloneDeep(application);

    const data = {
      Allgemein_Status: nachweisPositionStatus,
      Allgemein_Kostentraeger: kostentraeger,
      Allgemein_Unternehmen_Institution: organizationId,
      Allgemein_Kostenart: KOSTEN_POSITION.Ausgabe,
      Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Personalkosten,
    };
    set(
      appCopy,
      addIndexesToPath(
        P.Mittelabruf_Nachweispositions.path,
        [`${get(appCopy, P.Mittelabruf_Nachweispositions.path, []).length}`],
        ["Mittelabruf_Nachweispositions"],
      ),
      { attributes: data },
    );
    saveApplication(false, appCopy);
  };
  const handleDelete = async (id: number) => {
    let appCopy = cloneDeep(application);
    const allNachweispositionen = get(
      appCopy,
      P.Mittelabruf_Nachweispositions.path,
      [],
    );
    set(
      appCopy,
      P.Mittelabruf_Nachweispositions.path,
      allNachweispositionen.filter((item: any) => item.id !== id),
    );
    let res = await DeleteRecord(APIS.Nachweisposition, id);
    if (res) {
      saveApplication(false, appCopy);
    }
  };

  return (
    <>
      <InhaltWrapper marginBottom={"1rem"}>
        <Typography variant="h4" fontWeight={600}>
          {sectionTitle}
        </Typography>
        <Typography fontWeight={600}>
          {isGeplanteAusgaben ? (
            <>
              Bitte geben Sie hier die <u>geplanten Personalausgaben</u> für die
              nächsten drei Monate an:
            </>
          ) : (
            <>
              Bitte geben Sie hier die <u>bereits angefallenen</u>{" "}
              Personalausgaben an:
            </>
          )}
        </Typography>
      </InhaltWrapper>

      {(() => {
        let displayCounter = 0;
        // eslint-disable-next-line array-callback-return
        return get(application, P.Mittelabruf_Nachweispositions.path, []).map(
          (eachPosition: any, i: number) => {
            if (
              get(eachPosition, NP_P.Allgemein_Kostenart_Finanzierung.path) ===
                KOSTEN_ARTEN.Personalkosten &&
              get(eachPosition, NP_P.Allgemein_Status.path) === statusFilter &&
              belongsToMittelabrufOrganization(
                eachPosition,
                organizationId,
                kostentraeger,
              )
            ) {
              displayCounter++;
              const name = get(
                eachPosition,
                NP_P.Allgemein_Bezeichnung.path,
                "",
              );
              return (
                <>
                  <MUIAccordionComponent
                    key={i}
                    summaryTitle={`Personalausgabe ${displayCounter} ${name ? ` | ${name}` : ""}`}
                    onDelete={() => handleDelete(get(eachPosition, "id"))}
                    deleteButtonTooltip="Ausgabe löschen"
                  >
                    <CreateForm
                      formStruc={mapPersonalAusgaben(
                        formStruc(isGeplanteAusgaben),
                        i,
                      )}
                      formValues={application}
                      updateFormValuesFunc={setApplicationProperty}
                      readOnly={readOnly}
                      api={APIS.Nachweisposition}
                      size="small"
                    />
                  </MUIAccordionComponent>
                </>
              );
            }
          },
        );
      })()}

      <Button
        sx={{ mt: "2rem", minWidth: "28rem" }}
        endIcon={<AddIcon />}
        onClick={() => handleAdd(statusFilter)}
      >
        {addButtonLabel}
      </Button>

      <InhaltTrenner marginBottom="2rem" />
    </>
  );
};

export default PersonalausgabenLoeweInputMA;

const formStruc = (isGeplanteKosten?: boolean) => {
  let struc: FormStrucItem[] = [
    {
      fields: [
        ...(!isGeplanteKosten
          ? [
              P.Mittelabruf_Nachweispositions.schema.attributes
                .Allgemein_Datum_Der_Rechnung,
              {
                ...P.Mittelabruf_Nachweispositions.schema.attributes
                  .Allgemein_Datum_Der_Zahlung,
                label: "Datum der Zahlung / Werstellung",
              },
            ]
          : []),
        {
          ...P.Mittelabruf_Nachweispositions.schema.attributes
            .Stundennachweis_Gesamtstunden,
          label: "Anzahl Stunden",
        },
        P.Mittelabruf_Nachweispositions.schema.attributes
          .Stundennachweis_Stundensatz,
      ],
      userProps: { alignItems: "flex-end" },
    },
    {
      fields: [
        {
          ...P.Mittelabruf_Nachweispositions.schema.attributes
            .Allgemein_Ausgaben_Kosten,
          label: "zuwendungsfähige Ausgaben",
          disabled: true,
        },
      ],
    },
    {
      fields: [
        {
          ...P.Mittelabruf_Nachweispositions.schema.attributes
            .Allgemein_Bezeichnung,
          label: "Zweckbestimmung",
          tooltip: "(Name und Funktion)",
        },
      ],
      userProps: { columnToDivide: 1 },
    },
  ];
  return struc;
};

const mapPersonalAusgaben = (formStruc: any[], index: number) => {
  let result = formStruc.map((row) => {
    return {
      ...row,
      fields: (get(row, "fields", []) as Array<any>).map((value) => {
        let pathWithoutIndex = get(value, "path", "") as string;
        return {
          ...value,
          path:
            pathWithoutIndex.slice(
              0,
              P.Mittelabruf_Nachweispositions.path.length,
            ) +
            `.${index}` +
            pathWithoutIndex.slice(P.Mittelabruf_Nachweispositions.path.length),
        };
      }),
    };
  });
  return result;
};

export const getFormStrucPersonalausgabenLoeweInputMA = () => {
  return concat(formStruc());
};
